import { useState, useEffect, lazy, Suspense } from "react";
import { Hero } from "@/components/Hero";
import { StickyPricingBar } from "@/components/StickyPricingBar";
import { FAQ } from "@/components/FAQ";
import { PromoPopup } from "@/components/PromoPopup";
import { useTemplates } from "@/hooks/useTemplates";
import { useAuth } from "@/hooks/useAuth";
import { usePaymentStatus } from "@/hooks/usePaymentStatus";
import AuthModal from "@/components/AuthModal";
import { CssSpinner } from "@/components/CssSpinner";
import { Seo } from "@/components/Seo";
import { homeJsonLd } from "@/data/homeJsonLd";

// Lazy load heavy components
const IntegrationsMarquee = lazy(() => import("@/components/IntegrationsMarquee").then(m => ({ default: m.IntegrationsMarquee })));
const ByTheNumbers = lazy(() => import("@/components/ByTheNumbers").then(m => ({ default: m.ByTheNumbers })));
const EnhancedPricing = lazy(() => import("@/components/EnhancedPricing").then(m => ({ default: m.EnhancedPricing })));
const ResourcesVault = lazy(() => import("@/components/ResourcesVault").then(m => ({ default: m.ResourcesVault })));
const ClaudeSkills = lazy(() => import("@/components/ClaudeSkills").then(m => ({ default: m.ClaudeSkills })));
const CoursesSection = lazy(() => import("@/components/CoursesSection").then(m => ({ default: m.CoursesSection })));
const N8nTemplatesSection = lazy(() => import("@/components/N8nTemplatesSection").then(m => ({ default: m.N8nTemplatesSection })));
const WhiteLabelCtaSection = lazy(() => import("@/components/WhiteLabelCtaSection").then(m => ({ default: m.WhiteLabelCtaSection })));
import { SkoolPromoBanner } from "@/components/SkoolPromoBanner";

const Index = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const {
    templates,
    loading,
    error,
    searchTemplates
  } = useTemplates();
  const {
    user,
    loading: authLoading
  } = useAuth();
  const {
    hasAccess,
    loading: paymentLoading,
    markPaymentCompleted
  } = usePaymentStatus();

  // Get filtered templates based on search query
  const filteredTemplates = searchTemplates(searchQuery);

  // Show auth modal when needed
  useEffect(() => {
    if (!authLoading && !user) {
      const hasSeenAuth = localStorage.getItem('has_seen_auth');
      if (!hasSeenAuth) {
        setShowAuthModal(true);
        localStorage.setItem('has_seen_auth', 'true');
      }
    }
  }, [authLoading, user]);

  // Show pricing after successful auth
  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    setTimeout(() => {
      if (!hasAccess && !paymentLoading) {
        setShowPricing(true);
      }
    }, 1000);
  };

  // Handle successful payment
  const handlePurchaseSuccess = () => {
    markPaymentCompleted();
    setShowPricing(false);
    window.location.reload();
  };
  const handleSearchSubmit = () => {
    const templatesSection = document.getElementById('templates');
    if (templatesSection) {
      requestAnimationFrame(() => {
        templatesSection.scrollIntoView({
          behavior: 'smooth'
        });
      });
    }
  };

  // Render Hero immediately — auth/payment resolve in background (no blocking gate)
  return <div className="min-h-screen bg-background">
      <Seo path="/" jsonLd={homeJsonLd} />

      {/* Sticky Pricing Bar */}
      <StickyPricingBar />
      
      {/* Hero Section */}
      <Hero searchQuery={searchQuery} onSearchChange={setSearchQuery} onSearchSubmit={handleSearchSubmit} resultsCount={searchQuery ? filteredTemplates.length : undefined} />

      {/* All Courses (right after Hero) + Resources Vault + Claude Skills */}
      <Suspense fallback={<div className="py-20 flex justify-center"><CssSpinner size={48} /></div>}>
        <CoursesSection />
        <ResourcesVault />
        <ClaudeSkills />
        <N8nTemplatesSection />
      </Suspense>

      {/* Pricing - Choose Your Plan */}
      <Suspense fallback={<div className="py-20 flex justify-center"><CssSpinner size={48} /></div>}>
        <div id="pricing-section">
          <EnhancedPricing onPurchaseSuccess={handlePurchaseSuccess} />
        </div>
      </Suspense>

      {/* Lower priority sections - load after main content */}
      <Suspense fallback={<div className="py-20 flex justify-center"><CssSpinner size={48} /></div>}>
        {/* Integrations Marquee */}
        <IntegrationsMarquee />

        {/* Was an eight-quote testimonial carousel with invented names and a
            "10,000+ happy customers / 500+ integrations / 98% success rate" stat row.
            None of it was sourced. Replaced with counts read from the live tables. */}
        <ByTheNumbers />

        {/* FAQ Section */}
        <FAQ />

        {/* White Label CTA hidden pending Stripe migration of that offering */}
        {/* <WhiteLabelCtaSection /> */}
      </Suspense>

      {/* Skool promo bar before footer */}
      <SkoolPromoBanner />

      {/* Rotating promo popup — home page only */}
      <PromoPopup />

      {/* Auth Modal */}
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} onSuccess={handleAuthSuccess} />
    </div>;
};
export default Index;
